import { Injectable } from '@angular/core';
import {FISCAL_YEAR_END, FISCAL_YEAR_START} from '../../../constants';
import moment from 'moment';
import {Option} from 'fsx-component-library';

export interface State {
  label: string;
  value: string;
}

@Injectable({
  providedIn: 'root'
})

export class UtilitiesService {
  statesArray: State[] = [{"label":"AL","value":"AL"},{"label":"AK","value":"AK"},{"label":"AZ","value":"AZ"},{"label":"AR","value":"AR"},{"label":"CA","value":"CA"},{"label":"CO","value":"CO"},{"label":"CT","value":"CT"}, {"label": "DC", "value": "DC"}, {"label":"DE","value":"DE"},{"label":"FL","value":"FL"},{"label":"GA","value":"GA"},{"label":"HI","value":"HI"},{"label": "ID","value":"ID"},{"label":"IL","value":"IL"},{"label":"IN","value":"IN"},{"label":"IA","value":"IA"},{"label":"KS","value":"KS"},{"label":"KY","value":"KY"},{"label":"LA","value":"LA"},{"label":"ME","value":"ME"},{"label":"MD","value":"MD"},{"label":"MA","value":"MA"},{"label":"MI","value":"MI"},{"label":"MN","value":"MN"},{"label":"MS","value":"MS"},{"label":"MO","value":"MO"},{"label":"MT","value":"MT"},{"label":"NE","value":"NE"},{"label":"NV","value":"NV"},{"label":"NH","value":"NH"},{"label":"NJ","value":"NJ"},{"label":"NM","value":"NM"},{"label":"NY","value":"NY"},{"label":"NC","value":"NC"},{"label":"ND","value":"ND"},{"label":"OH","value":"OH"},{"label":"OK","value":"OK"},{"label":"OR","value":"OR"},{"label":"PA","value":"PA"},{"label":"RI","value":"RI"},{"label":"SC","value":"SC"},{"label":"SD","value":"SD"},{"label":"TN","value":"TN"},{"label":"TX","value":"TX"},{"label":"UT","value":"UT"},{"label":"VT","value":"VT"},{"label":"VA","value":"VA"},{"label":"WA","value":"WA"},{"label":"WV","value":"WV"},{"label":"WI","value":"WI"},{"label":"WY","value":"WY"}];

  constructor() { }

  getStates() {
    return this.statesArray;
  }

  /**
   *
   * @param baseUrl
   * @param params
   * @private
   */
  public buildUrlWithParams(baseUrl: string, params: any): string {
    const queryParams = Object.keys(params)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
    return `${baseUrl}?${queryParams}`;
  }

  /**
   * Adds an extra day to the end date for the search to be in the intended bounds. Returns result as a string.
   * @param date
   * @param daysBuffer
   */
  public applyDateBoundsString(date: string, daysBuffer: number): string {
    if (date != '') {
      const result = new Date(date);
      result.setDate(result.getDate() + daysBuffer);
      return  result.toISOString().split("T", 1)[0] || '';
    }
    return date;
  }

  /**
   * Adds an extra day to the end date for the search to be in the intended bounds. Returns result as a Date object.
   * @param endDate
   */
  public getEndDateFinalBoundsDate(endDate: Date): Date {
    const result = new Date(endDate);
    return new Date(result.setDate(result.getDate() + 1));
  }

  /**
   * Formats a date string to the format 'YYYY-MM-DD'. Returns result as a string.
   * @param date
   * @private
   */
  public formatDateString(date: Date): string {
    return moment(date).utc(true).toISOString().split("T", 1)[0] || '';
  }

  /**
   * Formats a date string to the format 'YYYY-MM-DD'. Returns result as a Date object.
   * @param date
   */
  public formatDate(date: Date): Date {
    return new Date(moment(date).utc(true).toISOString().split("T", 1)[0]);
  }

  /**
   * Dynamically creates an anchor element, sets the href to the provided url, appends the anchor element to the body,
   * clicks the anchor element, and removes the anchor element from the body.
   * @param url
   * @param target
   */
  public dynamicallyCreateAnchorAndNavigate(url: string, target?: string): void {
    let anchorElement = document.createElement('a');
    anchorElement.href = url;
    if (!!target) {
      anchorElement.target = target;
    }
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
  }

  /**
   * A simple method to see if using an iPhone by checking user agent
   */
  public isIphone(): boolean {
    const userAgent = navigator.userAgent;
    return /iPhone/.test(userAgent) || /iPhone Simulator/.test(userAgent);
  }

  /**
   * Method to check user agent and see if using an iPad.
   */
  public isIpad(): boolean {
    const userAgent = navigator.userAgent;
    return /iPad/.test(userAgent) || /iPad Simulator/.test(userAgent);
  }

  /**
   * Method to check if the device is in landscape mode.
   */
  public isLandscape(): boolean {
    return (window.innerWidth > window.innerHeight);
  }

  /**
   * Method to check if the browser is Chrome.
   */
  public isChrome(): boolean {
    return /Chrome/i.test(navigator.userAgent) || /CriOS/i.test(navigator.userAgent);
  }

  /**
   * Method to check if the browser is Safari.
   */
  public isSafari(): boolean {
    return /Safari/i.test(navigator.userAgent) && !/CriOS/i.test(navigator.userAgent);
  }

  /**
   * Method that removes all non-numeric characters from a string.
   * @param str
   */
  public removeNonNumericCharacters(str: string): string {
    return str.replace(/\D/g,'');
  }

  /**
   * Helper method to redact ssn/fein.
   * @param input
   */
  public redactSSNFEIN(input: string): string {
    // Ensure input is a string
    if (typeof input !== 'string') return '';

    // Remove non-numeric characters
    let cleaned = input.replace(/\D/g, '');

    // Check for SSN (9 digits) or FEIN (9 digits, but formatted differently)
    if (cleaned.length === 9) {
      return '*****' + cleaned.slice(-4);
    }

    // Check for FEIN with a different format (XX-XXXXXXX)
    if (input.match(/^\d{2}-\d{7}$/)) {
      return '**-*****' + cleaned.slice(-4);
    }

    // If it doesn't match expected formats, return original string
    return input;
  }

  /**
   * Method to get the fiscal year timestamps for the current and last fiscal year.
   * Uses the constants set in the constants file for the org's fiscal year.
   */
  public getFiscalYearTimestamps = () => {
    if (moment().quarter() == 4) {
      return {
        current: {
          start: moment().month(FISCAL_YEAR_START).startOf('month').toDate(),
          end: moment().add(1, 'year').month(FISCAL_YEAR_END).endOf('month').toDate()
        },
        last: {
          start: moment().subtract(1, 'year').month(FISCAL_YEAR_START).startOf('month').toDate(),
          end: moment().month(FISCAL_YEAR_END).endOf('month').toDate()
        }
      };
    } else {
      return {
        current: {
          start: moment().subtract(1, 'year').month(FISCAL_YEAR_START).startOf('month').toDate(),
          end: moment().month(FISCAL_YEAR_END).endOf('month').toDate()
        },
        last: {
          start: moment().subtract(2, 'year').month(FISCAL_YEAR_START).startOf('month').toDate(),
          end: moment().subtract(1, 'year').month(FISCAL_YEAR_END).endOf('month').toDate()
        }
      };
    }
  };

  /**
   * Helper method that returns all the UserTypes as options.
   * @param includeAll
   */
  public getUserTypesAsOptions(includeAll = true): Option[] {
    const userTypesAsOptions: Option[] = [
      // Admin Group
      {
        label: 'Court Admin',
        value: 'CourtAdmin'
      },
      {
        label: 'Clerk Admin',
        value: 'ClerkAdmin'
      },
      {
        label: 'Crime Victim Manager',
        value: 'CVManager'
      },
      // AG Group
      {
        label: 'Attorney General Admin (CV)',
        value: 'AGAdminCV'
      },
      {
        label: 'Attorney General Admin (CC)',
        value: 'AGAdminCC'
      },
      {
        label: 'Attorney General (CV)',
        value: 'AttorneyGeneralCV'
      },
      {
        label: 'Attorney General (CC)',
        value: 'AttorneyGeneralCC'
      },
      {
        label: 'Attorney General Staff (CV)',
        value: 'AGStaffCV'
      },
      {
        label: 'Attorney General Staff (CC)',
        value: 'AGStaffCC'
      },
      // Court Staff Group
      {
        label: 'Court Staff',
        value: 'CourtStaff'
      },
      // Clerk Staff Group
      {
        label: 'Clerk Staff',
        value: 'ClerkStaff'
      },
      // CV Staff Group
      {
        label: 'Crime Victim Staff',
        value: 'CVStaff'
      },
      // Judicial Group
      {
        label: 'Judge Admin',
        value: 'JudgeAdmin'
      },
      {
        label: 'Judge',
        value: 'Judge'
      },
      {
        label: 'Commissioner',
        value: 'Commissioner'
      },
      {
        label: 'Judicial Staff',
        value: 'JudicialStaff'
      },
      // External Group
      {
        label: 'Attorney',
        value: 'Attorney'
      },
      {
        label: 'Self-Represented',
        value: 'ProSe'
      }/*,  TODO:  Put this back when State Agency users are allowed
      {
        label: 'State Agency',
        value: 'StateAgency'
      },*/
      // Support Group
      // ToDo: 7/31/2025 - Disabled for this iteration
      // {
      //   label: 'FSX Support',
      //   value: 'FSXSupport'
      // },
    ];
    if (includeAll) {
      userTypesAsOptions.unshift({
        label: 'All',
        value: 'all'
      });
    }
    return userTypesAsOptions;
  }

  /**
   * Converts an array of MIME types to their corresponding file extensions
   * @param mimeTypes - Array of MIME type strings
   * @returns Array of file extension strings (with dot prefix)
   */
  public mimeTypesToExtensions(mimeTypes: string[]): string[] {
    // MIME type to extension mapping
    const mimeToExtensionMap: Record<string, string> = {
      // Images
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/bmp': '.bmp',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'image/tiff': '.tiff',
      'image/x-icon': '.ico',

      // Documents
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
      'text/plain': '.txt',
      'text/html': '.html',
      'text/css': '.css',
      'text/csv': '.csv',
      'application/rtf': '.rtf',

      // Archives
      'application/zip': '.zip',
      'application/x-rar-compressed': '.rar',
      'application/x-7z-compressed': '.7z',
      'application/x-tar': '.tar',
      'application/gzip': '.gz',

      // Audio
      'audio/mpeg': '.mp3',
      'audio/wav': '.wav',
      'audio/ogg': '.ogg',
      'audio/mp4': '.m4a',
      'audio/aac': '.aac',
      'audio/flac': '.flac',

      // Video
      'video/mp4': '.mp4',
      'video/mpeg': '.mpeg',
      'video/quicktime': '.mov',
      'video/x-msvideo': '.avi',
      'video/webm': '.webm',
      'video/x-flv': '.flv',

      // Code/Scripts
      'application/javascript': '.js',
      'application/json': '.json',
      'application/xml': '.xml',
      'text/xml': '.xml',
      'application/x-python': '.py',
      'text/x-python': '.py',
      'application/java': '.java',
      'text/x-csrc': '.c',
      'text/x-c++src': '.cpp',

      // Fonts
      'font/woff': '.woff',
      'font/woff2': '.woff2',
      'application/font-woff': '.woff',
      'application/x-font-ttf': '.ttf',
      'font/opentype': '.otf'
    };

    return mimeTypes.map(mimeType => {
      const extension = mimeToExtensionMap[mimeType.toLowerCase()];
      return extension || '.unknown';
    });
  }

  /**&
   *
   * @param enumObj
   * @param firstLabel
   */
  public enumToOptions(enumObj: any, firstLabel?: string): Option[] {
    return Object.entries(enumObj)
      .filter(([key, value]) => typeof value === 'number')
      .map(([key, value]) => ({
        label: key,
        value: String(value)
      }))
      .sort((a, b) => {
        if (firstLabel) {
          if (a.label === firstLabel) return -1;
          if (b.label === firstLabel) return 1;
        }
        return 0;
      });
  }
}
